import PropTypes from 'prop-types';
// External Imports
import { Component } from 'react';

// Internal Imports
import { isInProduct } from '../../utils/inProduct.mjs';
import { logger } from '../../utils/rollbar.mjs';
import { getGoBaseUrl, isTrackingDisabled } from '../../utils/url.mjs';
import { getScriptStatuses, loadWebToolsModuleIfNeeded } from '../../utils/webTools.mjs';

// Local Functions
function getAuthorSlug() {
  const authorLinkElement = document.querySelector('link[rel="author"][href]');
  if (!authorLinkElement) {
    return '';
  }
  const authorSlug = authorLinkElement.getAttribute('href').split('/').pop();
  return authorSlug || '';
}

function sendSpaNavigationData() {
  const authorSlug = getAuthorSlug();

  globalThis.dataLayer?.push?.({
    author: authorSlug,
    event: 'pageview',
  });
}

// Component Definition
class MarketingTracker extends Component {
  componentDidMount() {
    if (!isTrackingDisabled() && !isInProduct(globalThis.location.search)) {
      globalThis.requestAnimationFrame(() => {
        this.trackingInitializedPromise = this.initTracking();
        // This return is used for testing functionality but doesn't do anything for production code
        return this.trackingInitializedPromise;
      });
    }
  }

  componentDidUpdate(previousProps) {
    const { isContentLoading, location } = this.props;

    if (!isTrackingDisabled() && !isInProduct(globalThis.location.search)) {
      // Detect when the loading spinner disappears (meaning content is ready)
      const isUncachedPageLoad = previousProps.isContentLoading && !isContentLoading;

      // Detect when a page cached in the Redux store is being re-visited (isContentLoading is never true)
      const isReduxCachedPageLoad =
        !previousProps.isContentLoading &&
        !isContentLoading &&
        previousProps.location.pathname !== location.pathname;
      if (this.trackingInitializedPromise && (isUncachedPageLoad || isReduxCachedPageLoad)) {
        this.trackingInitializedPromise.then(() =>
          globalThis.requestAnimationFrame(() => sendSpaNavigationData()),
        );
      }
    }
  }

  initTracking = () => {
    const { magazineEditionLanguage } = this.props;
    // Add page's author name to Google Tag Manager dataLayer queue or empty string if no author
    const authorSlug = getAuthorSlug();
    globalThis.dataLayer = [{ author: authorSlug }];

    return loadWebToolsModuleIfNeeded().then(async () => {
      try {
        const marketingTracker = globalThis.MarketingTracker;
        if (!marketingTracker) {
          // We get a lot of those, so sampling until we understand it better
          if (Math.random() > 0.99) {
            logger.warning(
              'Marketing tracker unavailable after webtools initialisation',
              getScriptStatuses(),
            );
          }

          return;
        }
        const googleTagManager = marketingTracker.gtm;
        await googleTagManager.load();
        await googleTagManager.init({ locale: magazineEditionLanguage });

        const campaignsTracker = new marketingTracker.CampaignsTracker({
          // Babbel Campaigns App ("host" really refers to the expected URL prefix, not the hostname)
          host: getGoBaseUrl(),
        });
        campaignsTracker.trackUserClick();
      } catch (error) {
        if (error.message !== 'Page is private so GTM will not be called from this page') {
          throw error;
        }
      }
    });
  };

  // eslint-disable-next-line class-methods-use-this -- this is a renderless component
  render() {
    return null; // eslint-disable-line unicorn/no-null -- React expects null for empty rendering
  }
}

// Component Metadata
MarketingTracker.propTypes = {
  isContentLoading: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types -- use a TypeScript type eventually
  magazineEditionLanguage: PropTypes.string.isRequired,
};

// Module Exports
export { MarketingTracker };
